import React, { useState, useMemo } from 'react';
import { Box, MuiBox, Button, FlexBox, Grid, InputField, Typography, SelectDropdown } from '@kis-technologies/kis-ui';
import { CreateTicketTemplatePayload } from 'store/ticketTemplates';

import {
  getTicketTemplateInitialForm,
  ticketTemplateValidationSchema,
  getHasEmptyInput,
  getHasInvalidInput,
  TemplateTypeList
} from '../../utils';
import { useStyles } from '../../styled';
import { INVALID, VALID } from '../../constants'; // needs to be changed
import { ConfigCreateFormProps, TicketTemplateType } from '../../types';
import { getSortedList } from 'utils/stringFormat';

export const CreateTicketTemplateForm = (props: ConfigCreateFormProps) => {
  const { loading, error, resetError, onSubmit } = props;
  const classes = useStyles()();

  // SETS THE FORM VALUES AND VALIDATION VALUES FOR EACH FORM VALUE (INVALID | VALID)
  const initialForm = getTicketTemplateInitialForm();
  const [form, setForm] = useState(initialForm);
  const [validation, setValidation] = useState<{ [x: string]: string }>({});
  const [validationMessage, setValidationMessage] = useState<{ [x: string]: string | null }>({});
  const [selectedType, setSelectedType] = useState<TicketTemplateType | null>(null);

  // CHECKS INVALIDATIONS AND EMPTY INPUTS AND INITIAL VALUES
  const hasEmptyInput = useMemo(() => getHasEmptyInput(form), [form]);
  const hasInvalidInput = useMemo(() => getHasInvalidInput(validation), [validation]);

  // FORM SUBMIT FOR CREATION
  const onCreateClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();

    const payload: CreateTicketTemplatePayload = {
      type: form.type,
      template: form.template
    };

    onSubmit(payload);
  };

  const onInputChange = (
    key: keyof ReturnType<typeof getTicketTemplateInitialForm>,
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    e.preventDefault();
    resetError();
    // IF THERE WAS VALIDATION ERROR FOR SELECTED INPUT - RESET IT TO VALID
    if (validation[key] === INVALID) {
      setValidation({ ...validation, [key]: VALID });
      setValidationMessage({ ...validationMessage, [key]: null });
    }
    // UPDATE THE FORM OBJECT WITH THE CHANGE TO CORRESPONDING KEY VALUE PAIR
    setForm({ ...form, [key]: e.target.value });
  };

  const onTypeChange = (item: TicketTemplateType) => {
    setSelectedType(item);
    setForm({ ...form, type: item.code });
  };

  // FORM INPUT BLUR
  const onInputBlur = async (
    key: keyof ReturnType<typeof getTicketTemplateInitialForm>,
    e: React.ChangeEvent<HTMLInputElement>,
    value: string | null = null
  ) => {
    e.preventDefault();

    // TRIMS WHITE SPACE OF THE INPUT VALUES ONBLUR
    setForm({ ...form, [key]: e.target.value.trim() });

    let invalidMessage = null;

    // IF THE TRIMMED STRING VALUE HAS VALUE RUN VALIDATION AND SET INVALID MESSAGE
    if (value && value.toString().trim().length) {
      const result = await ticketTemplateValidationSchema
        .validate({ [key]: value && value.toString().trim() })
        .catch((err) => err);
      invalidMessage = result && result.message;
    }

    // SET VALIDATION TO BE VALID OR INVALID DEPENDING ON WHETHER INVALID MESSAGE EXISTS
    setValidation({ ...validation, [key]: invalidMessage ? INVALID : VALID });
    setValidationMessage({ ...validationMessage, [key]: invalidMessage });
  };

  // FORM INPUT VALIDATION
  const validate = (key: keyof ReturnType<typeof getTicketTemplateInitialForm>) => {
    return validationMessage[key];
  };

  // CONDITIONS FOR DISABLING SUBMIT BUTTON
  const buttonDisabled = hasEmptyInput || hasInvalidInput;

  return (
    <FlexBox justify="center" flexOverride="1 1 0%" padding="0 0 11">
      <FlexBox direction="column" fullWidth align="center" justify="center">
        <Grid className={classes.createClientInnerContainer}>
          <Box flexOverride="none" fullWidth margin="0 0 4">
            <Typography className={classes.createNewClientHeading}>Create New Ticket Template</Typography>
          </Box>
          <FlexBox fullWidth direction="row" align="center" justifyContent="space-between">
            <Typography fontSize={16} fontWeight={600} sx={{ padding: '0 0 0 24px' }}>
              type
            </Typography>
            <SelectDropdown
              placeholder="Select a type"
              items={getSortedList(TemplateTypeList as TicketTemplateType[], 'name')}
              selectedItem={selectedType as TicketTemplateType}
              onChange={(item: TicketTemplateType) => onTypeChange(item)}
              itemToString={(item) => (item ? item.name : '')}
            />
          </FlexBox>
          <MuiBox className={classes.createTicketTemplate}>
            <InputField
              multiline
              label="template"
              size="medium"
              sx={{ width: '522px', height: '400px', borderRadius: '25px' }}
              fullWidth
              placeholder="template"
              value={form.template}
              onChange={(e) => onInputChange('template', e)}
              rows={16}
              onBlur={(e) => onInputBlur('template', e, e.target.value.trim())}
              helperText={validate('template')}
              error={validation.template === INVALID}
            />
          </MuiBox>
        </Grid>

        {error && <Typography className={classes.createClientErrorText}>{error}</Typography>}

        {/* FORM SUBMIT */}
        <MuiBox className={classes.createClientButtonBox}>
          <Button
            disabled={buttonDisabled}
            customSize="large"
            onClick={onCreateClick}
            isLoading={loading}
            width={334}
            type="submit"
            className={classes.createClientButton}
          >
            Create Ticket Template
          </Button>
        </MuiBox>
      </FlexBox>
    </FlexBox>
  );
};
